import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  type Company,
  type CompanyLink,
  CompanySchema,
  type Contact,
  companyKeys,
  type Requisites,
} from '@/entities/company'
import { apiRequest } from '@/shared/api'

// Частичный PATCH компании: каждая секция (профиль / реквизиты / контакты /
// ссылки) шлёт только своё поле. Бэк применяет присланные поля, остальные не
// трогает.
export type CompanyPatch = {
  name?: string
  slug?: string
  requisites?: Requisites
  contacts?: Contact[]
  links?: CompanyLink[]
}

// Правка компании одним PATCH. Ответ — write-модель: тегов и счётчиков проектов
// в ней нет, поэтому её накладываем поверх кэшированной карточки, а не кладём
// вместо неё (иначе шапка моргает без тегов до следующего фетча). Список
// инвалидируем.
export function useEditCompany(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (patch: CompanyPatch): Promise<Company> =>
      CompanySchema.parse(
        await apiRequest({ method: 'PATCH', url: `/companies/${companyId}`, body: patch }),
      ),
    // Правку показываем сразу, не дожидаясь ответа: ждать сетевой round-trip
    // ради собственного ввода незачем. Ошибка откатывает снапшот.
    onMutate: async (patch) => {
      const key = companyKeys.detail(companyId)
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<Company>(key)
      if (prev) {
        const optimistic: Company = {
          ...prev,
          ...(patch.name !== undefined ? { name: patch.name } : {}),
          ...(patch.contacts !== undefined ? { contacts: patch.contacts } : {}),
          ...(patch.links !== undefined ? { links: patch.links } : {}),
          ...(patch.requisites !== undefined
            ? { requisites: { ...prev.requisites, ...patch.requisites } }
            : {}),
        }
        qc.setQueryData(key, optimistic)
        qc.setQueryData(companyKeys.bySlug(prev.slug), optimistic)
      }
      return { prev }
    },
    onError: (_err, _patch, ctx) => {
      if (ctx?.prev) {
        qc.setQueryData(companyKeys.detail(companyId), ctx.prev)
        qc.setQueryData(companyKeys.bySlug(ctx.prev.slug), ctx.prev)
      }
    },
    onSuccess: (company) => {
      const merge = (prev: Company | undefined) => (prev ? { ...prev, ...company } : company)
      qc.setQueryData(companyKeys.bySlug(company.slug), merge)
      qc.setQueryData(companyKeys.detail(company.id), merge)
      void qc.invalidateQueries({ queryKey: companyKeys.all })
    },
  })
}
